import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, Link } from 'react-router-dom';

import { CategoryBreadcrumb } from '../components/catalog/CategoryBreadcrumb';
import { CategoryCard } from '../components/catalog/CategoryCard';
import { ProductCard } from '../components/catalog/ProductCard';
import { Loader } from '../components/ui/Loader';
import { useLanguage } from '../hooks/useLanguage';
import { useGetCategoryQuery, useGetCategoryProductsQuery, useGetCategoriesQuery } from '../store/api/catalogApi';

export function CategoryPage() {
  const { t } = useTranslation();
  const { categoryId } = useParams<{ categoryId: string }>();
  const { currentLanguage } = useLanguage();
  const [page, setPage] = useState(1);
  const limit = 20;

  const { data: categoryData, isLoading: categoryLoading, error: categoryError } = useGetCategoryQuery(
    categoryId || '',
    { skip: !categoryId }
  );

  // جلب الفئات الفرعية (الأولاد)
  const { data: childrenCategoriesData, isLoading: childrenLoading } = useGetCategoriesQuery(
    { level: 'sub', parentId: categoryId || '' },
    { skip: !categoryId }
  );

  // حساب ما إذا كانت الفئة لديها أولاد
  const hasChildren = useMemo(() => {
    if (childrenLoading || !childrenCategoriesData) return undefined;
    return childrenCategoriesData.data.categories && childrenCategoriesData.data.categories.length > 0;
  }, [childrenCategoriesData, childrenLoading]);
  
  // جلب المنتجات فقط إذا لم تكن هناك فئات فرعية (بعد انتهاء جلب الفئات الفرعية)
  const shouldFetchProducts = !childrenLoading && hasChildren === false;
  const { data: productsData, isLoading: productsLoading, error: productsError } = useGetCategoryProductsQuery(
    { categoryId: categoryId!, page, limit },
    { skip: !categoryId || !shouldFetchProducts }
  );

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  if (categoryLoading || childrenLoading || (shouldFetchProducts && productsLoading)) {
    return (
      <div className="min-h-screen">
        <div className="flex justify-center items-center py-16">
          <Loader />
        </div>
      </div>
    );
  }

  if (categoryError || !categoryData || !categoryId) {
    return (
      <div className="min-h-screen">
        <div className="card-glass rounded-2xl shadow-2xl p-8 text-center">
          <h2 className="text-2xl font-heading font-bold text-danger mb-4">
            {t('catalog.category.error.title')}
          </h2>
          <p className="text-neutral-medium font-body mb-6">
            {'data' in (categoryError as { data?: { message?: string } })
              ? ((categoryError as { data?: { message?: string } })?.data?.message || t('errors.network'))
              : t('errors.network')}
          </p>
          <Link
            to="/catalog"
            className="inline-block px-6 py-2 bg-brand-accent text-brand-primary font-heading font-semibold rounded-lg hover:shadow-lg hover:shadow-brand-accent/50 transform hover:scale-105 transition-all duration-300"
          >
            {t('catalog.category.error.back')}
          </Link>
        </div>
      </div>
    );
  }

  const category = categoryData.data.category;
  const childrenCategories = childrenCategoriesData?.data.categories || [];
  const products = productsData?.data.products || [];
  const pagination = productsData?.data.pagination;

  const categoryName = currentLanguage === 'ar' ? category.name : category.nameEn;
  const categoryDescription = currentLanguage === 'ar' 
    ? category.description 
    : category.descriptionEn;

  return (
    <div className="min-h-screen">
      {/* Breadcrumb */}
      <CategoryBreadcrumb category={category} />

      {/* Category Header */}
      <div className="mb-8 animate-slide-up">
        <h1 className="text-4xl md:text-5xl font-heading font-bold text-brand-accent mb-4">
          {categoryName}
        </h1>
        {categoryDescription && (
          <p className="text-neutral-medium text-lg font-body mb-4">
            {categoryDescription}
          </p>
        )}

        {/* Category Info - نعرض السعر والكمية فقط إذا لم تكن هناك فئات فرعية */}
        {hasChildren === false && (
          <div className="flex flex-wrap gap-4 mb-6">
            <div className="card-glass rounded-lg p-4">
              <span className="text-sm text-neutral-medium font-body block mb-1">
                {t('catalog.category.price')}
              </span>
              <span className="text-xl font-heading font-bold text-brand-accent">
                {category.price.toFixed(2)} USD
              </span>
            </div>
            <div className="card-glass rounded-lg p-4">
              <span className="text-sm text-neutral-medium font-body block mb-1">
                {t('catalog.category.available')}
              </span>
              <span className="text-xl font-heading font-bold text-neutral-light">
                {category.availableProductsCount}
              </span>
            </div>
            {category.isEnabledCashback && (
              <div className="bg-brand-accent/20 backdrop-blur-sm rounded-lg p-4 border border-brand-accent/30">
                <span className="text-sm text-brand-accent font-body block mb-1">
                  {t('catalog.category.cashback')}
                </span>
                <span className="text-xl font-heading font-bold text-brand-accent">
                  {(category.cashbackPercentage * 100).toFixed(0)}%
                </span>
              </div>
            )}
          </div>
        )}
      </div>

      {/* إذا كانت الفئة لديها أولاد، نعرض الفئات الفرعية */}
      {hasChildren === true ? (
        <div>
          {childrenCategories.length === 0 ? (
            <div className="card-glass rounded-2xl shadow-2xl p-8 text-center">
              <p className="text-neutral-medium text-lg font-body">
                {t('catalog.empty')}
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 animate-scale-in">
              {childrenCategories.map((childCategory) => (
                <CategoryCard key={childCategory._id} category={childCategory} />
              ))}
            </div>
          )}
        </div>
      ) : hasChildren === false ? (
        /* إذا لم تكن هناك فئات فرعية، نعرض المنتجات مع السعر والكمية */
        <>
          {productsError ? (
            <div className="card-glass rounded-2xl shadow-2xl p-8 text-center">
              <p className="text-neutral-medium font-body">
                {t('catalog.products.error')}
              </p>
            </div>
          ) : products.length === 0 ? (
            <div className="card-glass rounded-2xl shadow-2xl p-8 text-center">
              <p className="text-neutral-medium text-lg font-body">
                {t('catalog.products.empty')}
              </p>
            </div>
          ) : (
            <>
              {/* Products Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8 animate-scale-in">
                {products.map((product) => (
                  <ProductCard key={product._id} product={product} />
                ))}
              </div>

              {/* Pagination */}
              {pagination && pagination.pages > 1 && (
                <div className="flex items-center justify-center gap-2 mb-6">
                  <button
                    onClick={() => handlePageChange(pagination.page - 1)}
                    disabled={pagination.page === 1}
                    className="px-4 py-2 bg-neutral-dark text-neutral-light font-heading font-semibold rounded-lg hover:bg-neutral-dark/80 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {t('catalog.pagination.previous')}
                  </button>
                  
                  <div className="px-4 py-2 bg-neutral-dark/50 text-neutral-light font-body rounded-lg">
                    {t('catalog.pagination.page')} {pagination.page} {t('catalog.pagination.of')} {pagination.pages}
                  </div>
                  
                  <button
                    onClick={() => handlePageChange(pagination.page + 1)}
                    disabled={pagination.page >= pagination.pages}
                    className="px-4 py-2 bg-neutral-dark text-neutral-light font-heading font-semibold rounded-lg hover:bg-neutral-dark/80 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {t('catalog.pagination.next')}
                  </button>
                </div>
              )}

              {/* Pagination Info */}
              {pagination && (
                <div className="text-center text-neutral-medium font-body mb-6">
                  {t('catalog.pagination.showing')} {products.length} {t('catalog.pagination.of')} {pagination.total} {t('catalog.pagination.products')}
                </div>
              )}
            </>
          )}
        </>
      ) : null}
    </div>
  );
}

